import React, { useState } from 'react';

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  className?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

const FormInput: React.FC<FormInputProps> = ({
  label,
  error,
  helperText,
  className = '',
  icon,
  iconPosition = 'left',
  type = 'text',
  id,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  const isPasswordType = type === 'password';
  const inputType = isPasswordType && showPassword ? 'text' : type;

  return (
    <div className={`group ${className}`}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-semibold text-neutral-700 mb-3 transition-colors duration-200 group-focus-within:text-primary-600"
        >
          {label}
          {props.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {/* Left Icon */}
        {icon && iconPosition === 'left' && (
          <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-400 group-focus-within:text-primary-500 transition-colors duration-200">
            {icon}
          </div>
        )}

        <input
          id={inputId}
          type={inputType}
          className={`
            w-full px-4 py-4 
            ${icon && iconPosition === 'left' ? 'pl-12' : ''}
            ${icon && iconPosition === 'right' ? 'pr-12' : ''}
            ${isPasswordType ? 'pr-12' : ''}
            bg-white border-2 rounded-2xl 
            text-neutral-900 placeholder-neutral-400
            transition-all duration-200 ease-out
            focus:outline-none focus:ring-4 focus:ring-primary-500/20
            ${error
              ? 'border-red-300 focus:border-red-500 bg-red-50/50'
              : isFocused
              ? 'border-primary-500 shadow-glow'
              : 'border-neutral-200 hover:border-neutral-300'
            }
            ${props.disabled ? 'bg-neutral-50 cursor-not-allowed opacity-60' : ''}
          `.replace(/\s+/g, ' ').trim()}
          onFocus={(e) => {
            setIsFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            props.onBlur?.(e);
          }}
          {...props}
        />

        {/* Right Icon or Password Toggle */}
        {(icon && iconPosition === 'right') || isPasswordType ? (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
            {isPasswordType ? (
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-neutral-400 hover:text-neutral-600 focus:text-primary-500 transition-colors duration-200 focus:outline-none"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            ) : (
              <div className="text-neutral-400 group-focus-within:text-primary-500 transition-colors duration-200">
                {icon}
              </div>
            )}
          </div>
        ) : null}

        {/* Focus ring animation */}
        {isFocused && !error && (
          <div className="absolute inset-0 rounded-2xl border-2 border-primary-500 animate-pulse-soft pointer-events-none" />
        )}
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="mt-2 flex items-center gap-2 text-red-600 animate-fade-in-up">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}
      
      {/* Helper Text */}
      {helperText && !error && (
        <p className="mt-2 text-sm text-neutral-500 flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {helperText}
        </p>
      )}
    </div>
  );
};

export default FormInput;